// eslint-disable-next-line no-restricted-imports
import Moment from "moment";

const currencyCache = {};
export const currencyFormatter = (
  currencyCode: string,
  currencyDisplay: "code" | "symbol" | "narrowSymbol" | "name" = "symbol"
) => {
  const s = currencyCode + " - " + currencyDisplay;
  if (!currencyCache[s]) {
    const formatOptions = {
      style: "currency",
      currency: currencyCode,
      currencyDisplay,
    };
    try {
      currencyCache[s] = new Intl.NumberFormat(undefined, formatOptions as Intl.NumberFormatOptions);
    } catch {
      // we are running in a browser that doesn't support "narrowSymbol"
      currencyCache[s] = new Intl.NumberFormat(undefined, {
        ...formatOptions,
        currencyDisplay: "symbol",
      } as Intl.NumberFormatOptions);
    }
  }
  return currencyCache[s];
};

const currencyWithoutDecimalsCache = {};
export const currencyFormatterWithoutDecimals = (currencyCode: string, currencyDisplay = "symbol") => {
  const s = currencyCode + " - " + currencyDisplay;
  if (!currencyWithoutDecimalsCache[s]) {
    currencyWithoutDecimalsCache[s] = new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: currencyCode,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
      // currencyDisplay is being weird here
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore TS2345
      currencyDisplay,
    });
  }
  return currencyWithoutDecimalsCache[s];
};

export const percentFormatter = new Intl.NumberFormat(undefined, {
  style: "percent",
  minimumFractionDigits: 1,
});

export const percentFormatterWithoutDecimals = new Intl.NumberFormat(undefined, {
  style: "percent",
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

export const percentFormatterWithDecimals = new Intl.NumberFormat(undefined, {
  style: "percent",
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

export const percentFormatterWithTwoDP = new Intl.NumberFormat(undefined, {
  style: "percent",
  minimumFractionDigits: 2,
});

export const fmtPercentage = (value, withDecimals = false) => {
  return (withDecimals ? percentFormatterWithDecimals : percentFormatterWithoutDecimals).format(value / 100);
};

export interface FormatOptions {
  currencyDisplay?: "symbol" | "narrowSymbol";
  divisor?: number;
}

const defaultOptions: Required<FormatOptions> = {
  currencyDisplay: "symbol",
  divisor: 100,
};

export const applyDefaults = <T>(defaultOptions: Required<T>, options?: T): Required<T> => {
  return { ...defaultOptions, ...options };
};

export const fmtAmount = (amount: number, currency: string, options?: FormatOptions) => {
  const opts = applyDefaults<FormatOptions>(defaultOptions, options);
  return currencyFormatter(currency, opts.currencyDisplay).format(amount / opts.divisor);
};

export const fmtAmountWithoutDecimals = (amount: number, currency: string, options?: FormatOptions) => {
  const opts = applyDefaults<FormatOptions>(defaultOptions, options);
  return currencyFormatterWithoutDecimals(currency, opts.currencyDisplay).format(amount / opts.divisor);
};

export const roundToNumDecimals = (num: number, decimals = 2, roundDown = false) => {
  const mult = Math.pow(10, decimals);
  return (roundDown ? Math.floor : Math.round)(num * mult) / mult;
};

export function getFriendlyTimeStrings(value) {
  const enterDate = Moment(value);
  const browserTz = Intl.DateTimeFormat("en", { timeZoneName: "short" })
    .formatToParts()
    .find((p) => p.type === "timeZoneName")?.value;
  const utcDate = enterDate.utc();
  return {
    browserTz: `${enterDate.format("lll")} ${browserTz}`,
    utc: `${utcDate.format("lll")} UTC`,
  };
}

export const numberShortForm = (num: number | null) => {
  /**
   * Return string representation of the number in short form, replacing zeros with K, M etc.
     100,000.00 -> 100K, or 10,000,000.00 -> 10M
   */
  if (!num) {
    return "";
  }

  if (Math.abs(num) > 999499) {
    return Number.parseFloat((Math.sign(num) * (Math.abs(num) / 1000000)).toFixed(1)) + "M";
  }
  if (Math.abs(num) > 999) {
    return (Math.sign(num) * (Math.abs(num) / 1000)).toFixed(0) + "K";
  }
  return num.toString();
};

export const getCurrencySymbol = (
  currencyCode: string,
  currencyDisplay: "symbol" | "narrowSymbol" = "symbol"
): string => {
  return currencyFormatter(currencyCode, currencyDisplay).format(0).replace("0.00", "");
};
